import { Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, MapPin, ArrowRight, CheckCircle2, Star, Video, GraduationCap, ShieldCheck, MessageCircle, BadgeDollarSign, Clock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { INTERNATIONAL_CITIES as INTL_CITIES_WAVE_1, COUNTRY_HUB_URL as COUNTRY_HUB_WAVE_1 } from "../data/internationalCitiesData";
import { INTERNATIONAL_CITIES_BATCH_2B, COUNTRY_HUB_URL_BATCH_2B } from "../data/internationalCitiesBatch2B";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { buildIntlCityAEO } from "../data/aeoContent";
import RotatingTestimonials from "../components/RotatingTestimonials";
import { getTestimonialsForSlug } from "../data/testimonialsPool2G";

// Merge Wave-1 + Wave-2 Batch-2B international city dictionaries.
// New batches are appended here — never overwrite existing keys.
const INTERNATIONAL_CITIES = { ...INTL_CITIES_WAVE_1, ...INTERNATIONAL_CITIES_BATCH_2B };
const COUNTRY_HUB_URL = { ...COUNTRY_HUB_WAVE_1, ...COUNTRY_HUB_URL_BATCH_2B };

const ENQUIRY_PHONE = "+91 7000530316";
const ENQUIRY_PHONE_RAW = "917000530316";
const WA_LINK = `https://wa.me/${ENQUIRY_PHONE_RAW}?text=Hi%20Anand%20Tutorials%2C%20I%20need%20a%20free%20demo%20class.`;

export default function InternationalCity({ citySlug }) {
    const data = INTERNATIONAL_CITIES[citySlug];
    if (!data) return <Navigate to="/" replace />;

    const canonicalPath = `/${data.urlSlug}`;
    const canonicalUrl = `https://anandtutorials.com${canonicalPath}`;
    const countryHub = COUNTRY_HUB_URL[data.countryCode] || "/online-tuition-worldwide";

    // Batch 2N (Feb-2026) — International city AEO (QuickAnswer + Speakable FAQ + FAQPage schema)
    const primaryCurriculumSlug = data.relatedCourses && data.relatedCourses.length > 0 ? data.relatedCourses[0].path.replace("/courses/", "") : "ib";
    const cityAeo = buildIntlCityAEO({
        name: data.name,
        country: data.country,
        currency: data.currency,
        primaryCurriculumSlug,
    });
    const aeoFaqSchema = buildConversationalFAQSchema(cityAeo.faqs);

    // ── Schemas (EducationalOrganization + Service + Course + WebPage + BreadcrumbList + FAQPage + Speakable) ──
    const eduOrgSchema = {
        "@context": "https://schema.org",
        "@type": ["EducationalOrganization", "OnlineBusiness"],
        "@id": `${canonicalUrl}#eduorg`,
        "name": `Anand Tutorials — 1:1 Online Tuition in ${data.name}, ${data.country}`,
        "alternateName": [`Online tutor ${data.name}`, `Private tutor ${data.name}`, `${data.name} online tuition`, `Indian tutor for ${data.name} students`],
        "url": canonicalUrl,
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "image": "https://anandtutorials.com/logo-anand-tutorials.png",
        "telephone": ENQUIRY_PHONE,
        "email": "info@anandtutorials.com",
        "areaServed": [
            { "@type": "City", "name": data.name, "containedInPlace": { "@type": "AdministrativeArea", "name": data.state } },
            { "@type": "Country", "name": data.country },
        ],
        "knowsAbout": data.pillars.map((p) => p.t).slice(0, 6),
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.rating.value, "reviewCount": data.rating.count,
            "bestRating": "5", "worstRating": "1",
        },
    };
    const serviceSchema = {
        "@context": "https://schema.org", "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        "serviceType": "1:1 Live Online Tuition",
        "provider": { "@id": `${canonicalUrl}#eduorg` },
        "areaServed": { "@type": "City", "name": data.name },
        "description": data.seoDescription,
        "audience": { "@type": "EducationalAudience", "educationalRole": "student" },
    };
    const courseSchema = {
        "@context": "https://schema.org", "@type": "Course",
        "name": `1:1 Live Online Tuition for ${data.name}, ${data.country} students`,
        "description": data.intro.slice(0, 400),
        "provider": { "@id": `${canonicalUrl}#eduorg` },
        "inLanguage": data.langCode,
        "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "location": { "@type": "Place", "name": data.name },
        },
    };
    const webPageSchema = {
        "@context": "https://schema.org", "@type": "WebPage",
        "@id": canonicalUrl, "url": canonicalUrl,
        "name": data.seoTitle, "description": data.seoDescription,
        "inLanguage": data.langCode,
        "isPartOf": { "@id": "https://anandtutorials.com/#website" },
        "primaryImageOfPage": "https://anandtutorials.com/logo-anand-tutorials.png",
    };
    const breadcrumbSchema = {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Online Tuition Worldwide", "item": "https://anandtutorials.com/online-tuition-worldwide" },
            { "@type": "ListItem", "position": 3, "name": data.country, "item": `https://anandtutorials.com${countryHub}` },
            { "@type": "ListItem", "position": 4, "name": data.name, "item": canonicalUrl },
        ],
    };
    const faqSchema = {
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question", "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };
    const speakableSchema = {
        "@context": "https://schema.org", "@type": "SpeakableSpecification",
        "cssSelector": [".speakable-hero", ".speakable-quick-answer", ".speakable-primary-cta"],
    };

    // ── AEO Content ──
    // Country-specific curriculum snippet for the AEO answer.  Extend for
    // Wave-2 Batch-2B (Saudi/Qatar/Kuwait/Oman/Germany/Ireland/France/NZ) and
    // fall back to a generic "curriculum-matched" phrase for unknown countries.
    const _curriculumByCountry = {
        "USA": "AP, IB, Common Core and SAT",
        "UK": "GCSE, A-Level and IB",
        "UAE": "CBSE, ICSE, IB, IGCSE and MoE",
        "Canada": "Ontario Curriculum (OSSD), IB and AP",
        "Australia": "HSC, IB and IGCSE",
        "Saudi Arabia": "CBSE, ICSE, IB, Cambridge IGCSE/A-Level and MoE Saudi",
        "Qatar": "CBSE, ICSE, IB, Cambridge IGCSE/A-Level and Qatar MoE",
        "Kuwait": "CBSE, ICSE, IB, Cambridge IGCSE/A-Level and Kuwait MoE",
        "Oman": "CBSE, ICSE, IB, Cambridge IGCSE/A-Level and Oman MoE",
        "Germany": "Abitur (in English), IB DP and Cambridge IGCSE/A-Level",
        "Ireland": "Leaving Certificate, Junior Cycle, IB and Cambridge IGCSE",
        "France": "Baccalauréat spécialités, OIB/BFI, IB DP and Cambridge",
        "New Zealand": "NCEA (L1/L2/L3), Cambridge IGCSE/A-Level and IB DP",
    };
    const _curriculumBlurb = _curriculumByCountry[data.country] || "the local school curriculum, plus IB and Cambridge";
    const aeoQuestion = `How can students in ${data.name}, ${data.country} get 1:1 online tuition from Anand Tutorials?`;
    const aeoAnswer = `Anand Tutorials delivers 1:1 live online tuition to ${data.name} students via Zoom or Google Meet — matched tutors for ${_curriculumBlurb} — at 40-80% below local ${data.name} private-tuition rates. Time-zone matched to ${data.timezone} evening slots. Pay in ${data.currency}. Book a free 45-60 minute 1:1 demo class — no card required — by calling ${ENQUIRY_PHONE} or filling the form on this page.`;

    const conversationalFaqs = data.faqs.slice(0, 4).map(([q, a]) => ({ question: q, answer: a }));
    const convFaqSchema = buildConversationalFAQSchema(conversationalFaqs);

    return (
        <>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                canonical={canonicalPath}
            />
            <Helmet>
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:locale" content={data.langCode.replace("-", "_")} />
                <script type="application/ld+json">{JSON.stringify(eduOrgSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(speakableSchema)}</script>
                {convFaqSchema && <script type="application/ld+json">{JSON.stringify(convFaqSchema)}</script>}
                {/* Batch 2N — City AEO FAQPage schema */}
                <script type="application/ld+json">{JSON.stringify(aeoFaqSchema)}</script>
            </Helmet>

            <PageHeader
                title={data.h1}
                subtitle={`1:1 Live Online · ${data.name}, ${data.state} · ${data.currency} billing · ${data.timezone}`}
                data-testid={`intl-city-header-${data.slug}`}
            />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <Breadcrumbs
                    items={[
                        { name: "Home", href: "/" },
                        { name: "Online Tuition Worldwide", href: "/online-tuition-worldwide" },
                        { name: data.country, href: countryHub },
                        { name: data.name, current: true },
                    ]}
                />
            </div>

            {/* Hero + quick-answer AEO */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                    <p className="text-lg text-gray-700 leading-relaxed" data-testid={`intl-city-intro-${data.slug}`}>{data.intro}</p>
                    <QuickAnswerCard question={aeoQuestion} answer={aeoAnswer} className="speakable-quick-answer" />
                    <div className="flex flex-wrap gap-3">
                        <a
                            href={`tel:+${ENQUIRY_PHONE_RAW}`}
                            className="speakable-primary-cta inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white font-semibold px-6 py-3"
                            data-testid={`intl-city-call-cta-${data.slug}`}
                        >
                            <Phone className="w-4 h-4" /> Call {ENQUIRY_PHONE}
                        </a>
                        <a
                            href={WA_LINK} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3"
                            data-testid={`intl-city-whatsapp-cta-${data.slug}`}
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp for free demo
                        </a>
                    </div>
                </div>
                <div className="md:col-span-1">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4 sticky top-24">
                        <div className="flex items-center gap-3">
                            <span className="text-3xl">{data.flag}</span>
                            <div>
                                <div className="text-sm text-gray-500">Serving</div>
                                <div className="font-bold text-gray-900">{data.name}, {data.country}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-600" /> {data.timezone}</div>
                            <div className="flex items-center gap-2"><BadgeDollarSign className="w-4 h-4 text-brand-600" /> {data.currency}</div>
                            <div className="flex items-center gap-2"><Video className="w-4 h-4 text-brand-600" /> 1:1 Live</div>
                            <div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> {data.rating.value} · {data.rating.count} reviews</div>
                        </div>
                        <RegistrationForm compact source={`intl-city-${data.slug}`} data-testid={`intl-city-form-${data.slug}`} />
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Why {data.name} families choose Anand Tutorials</h2>
                <div className="grid md:grid-cols-2 gap-6">
                    {data.pillars.map((p, i) => (
                        <div key={i} className="rounded-xl border border-gray-200 p-6 bg-white shadow-sm">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-1">{p.t}</h3>
                                    <p className="text-sm text-gray-700 leading-relaxed">{p.d}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Batch 2N — City AEO Quick-Answer + Speakable FAQ */}
            <QuickAnswerCard
                question={cityAeo.quickAnswer.question}
                answer={cityAeo.quickAnswer.answer}
                bullets={cityAeo.quickAnswer.bullets}
                footer={cityAeo.quickAnswer.footer}
                testId={`intl-city-quick-answer-${data.slug}`}
            />

            {/* Boroughs / neighbourhoods */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-brand-600" /> {data.name} neighbourhoods we serve online
                </h2>
                <div className="flex flex-wrap gap-2">
                    {data.boroughs.map((b) => (
                        <span key={b} className="rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-sm px-3 py-1">{b}</span>
                    ))}
                </div>
            </section>

            {/* Batch 2N — City AEO Conversational FAQ (currency + primary-curriculum aware) */}
            <ConversationalFAQ
                heading={`Common questions ${data.name} parents ask us`}
                subheading={`Direct, no-filler answers to the questions Google's AI Overviews most often need — matched to your ${data.currency} billing and ${data.name} school ecosystem.`}
                items={cityAeo.faqs}
                testId={`intl-city-conversational-faq-${data.slug}`}
            />

            {/* Popular courses in this city — Batch 2I Part 4 reverse cross-links */}
            {data.relatedCourses && data.relatedCourses.length > 0 && (
                <section
                    className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
                    data-testid={`intl-city-${data.slug}-related-courses`}
                >
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-600 mb-3">Popular in {data.name}</div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">The 1:1 course tracks {data.name} parents ask for most.</h2>
                    <p className="text-gray-700 mb-6 max-w-3xl">
                        Each pillar below is our dedicated 1:1 mentor track — every tutor is board-code-matched, syllabus-fluent and rate-transparent in {data.currency}. Tap any card to see the exam-pattern deep-dive, sample outcomes, mid-year plan and Q&amp;A for that track.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.relatedCourses.map((rc, i) => (
                            <Link
                                key={rc.path}
                                to={rc.path}
                                data-testid={`intl-city-${data.slug}-course-${i}`}
                                className="group flex items-start gap-3 p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 hover:border-emerald-500 hover:shadow-sm transition-all"
                            >
                                <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                    <GraduationCap className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-heading font-semibold text-gray-900 text-sm mb-1 group-hover:text-emerald-700">
                                        {rc.label} in {data.name}
                                    </div>
                                    <div className="text-xs text-gray-600 leading-relaxed">{rc.tagline}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Testimonials — rotating pool (Batch 2G), or single fallback */}
            {(() => {
                const pool = getTestimonialsForSlug(citySlug);
                const items = pool.length >= 2 ? pool : (data.testimonial ? [data.testimonial] : []);
                if (items.length === 0) return null;
                return <RotatingTestimonials items={items} variant="light" heading={`What ${data.name} parents say`} />;
            })()}

            {/* FAQs (conversational + regular) */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Frequently asked — {data.name} online tuition</h2>
                <ConversationalFAQ faqs={conversationalFaqs} />
                <div className="mt-6 space-y-3">
                    {data.faqs.slice(4).map(([q, a], i) => (
                        <details key={i} className="rounded-lg border border-gray-200 bg-white p-4">
                            <summary className="font-semibold text-gray-900 cursor-pointer">{q}</summary>
                            <p className="mt-2 text-gray-700 text-sm leading-relaxed">{a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* Cross-linking cluster */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Related resources</h2>
                <div className="grid md:grid-cols-3 gap-4">
                    <Link to={countryHub} className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">All 1:1 online tuition in {data.country}</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                    <Link to="/online-tuition-worldwide" className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Global 1:1 online tuition hub</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                    <Link to="/free-demo-class-online-tuition" className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">Book your free 1:1 demo</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                    <Link to="/foreign-students" className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">NRI &amp; foreign student program</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                    <Link to="/how-1-on-1-online-tuition-works" className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">How our 1:1 process works</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                    <Link to="/courses" className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 flex items-center justify-between">
                        <span className="font-semibold text-gray-900">All courses &amp; boards</span>
                        <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                </div>
            </section>

            {/* Final CTA */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="rounded-xl bg-brand-800 text-white p-8 text-center">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to book a free 1:1 demo class for your {data.name} student?</h2>
                    <p className="text-brand-100 mb-6">Zero cost. Zero card. Zero commitment. A real tutor. A real class.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a
                            href={`tel:+${ENQUIRY_PHONE_RAW}`}
                            className="inline-flex items-center gap-2 rounded-lg bg-white text-brand-800 hover:bg-brand-50 font-semibold px-6 py-3"
                            data-testid={`intl-city-final-call-${data.slug}`}
                        >
                            <Phone className="w-4 h-4" /> Call {ENQUIRY_PHONE}
                        </a>
                        <a
                            href={WA_LINK} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3"
                            data-testid={`intl-city-final-whatsapp-${data.slug}`}
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp for free demo
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}
